import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippy.js/react';
import { useTranslation } from 'react-i18next';
import {
    IconBroadcast,
    IconAssetOffline,
    IconAssetOnline,
    IconAssetWarning,
} from 'svgIcons/MotionPortalIcons';
import { LoadingIndicator } from 'sharedComponents';
import { apiCallStatus } from 'helpers/constants';
import { assetOnlineStatus, GATWAY_CONNECTIVITY_STATUS } from './constants';
import { getTimeSinceDateString } from 'helpers/dateHelper';

const GatewayListManagementComponent = ({ gatewayDetails, onSelectedGatewayData, isShowGatewayDetails, gatewayID }) => {
    const { t: translate } = useTranslation();
    const { gatewayList = [], loadingStatus } = gatewayDetails || {};
    const [activeIndex, setActiveIndex] = useState(null);

    const handleSelectedGateway = (id, status, timeStamp) => {
        onSelectedGatewayData({ id, status, timeStamp });
    };

    useEffect(() => {
        // Sync activeIndex with gatewayID from parent
        // This ensures only one item is active at a time
        setActiveIndex(gatewayID || null);
    }, [gatewayID]);

    if (loadingStatus === apiCallStatus.ERROR) {
        // Handle failure state (optional)
        return <div>{translate('ABB.Powertrain.Frontend.errorGatewayList')}</div>;
    }

    const renderConnectivityIcon = (status) => {
        if (status === GATWAY_CONNECTIVITY_STATUS.CONNECTED) { return <IconAssetOnline />; }
        if (status === GATWAY_CONNECTIVITY_STATUS.DISCONNECTED) { return <IconAssetOffline />; }
        if (status === GATWAY_CONNECTIVITY_STATUS.CONNECTED_WITH_WARNING) { return <IconAssetWarning />; }
        return null;
    };

    const renderGatewayItems = () => {
        if (gatewayList.length === 0) {
            return (
                <li className='gateway-list__empty-label'>
                    {translate('ABB.Powertrain.Frontend.emptyGatewayResultsMessage')}
                </li>
            );
        }
        return gatewayList.map((gatewayItem) =>
            <li
                key={gatewayItem?.gatewayIdentifier}
                className={`gateway-list__item ${activeIndex === gatewayItem?.gatewayIdentifier && isShowGatewayDetails ? 'gateway-list__item--active' : ''}`}
            >
                <button
                    className='content-left'
                    onClick={() => handleSelectedGateway(
                        gatewayItem?.gatewayIdentifier,
                        gatewayItem?.summaryConnectionStatus,
                        gatewayItem.statusChangeTimestamp
                    )}
                >
                    <IconBroadcast />
                    <div className='gateway-details'>
                        <span className='gateway-details__name'>
                            {gatewayItem?.gatewayName || ''}
                        </span>
                        <span className='gateway-details__serialno'>
                            {gatewayItem?.gatewaySerialNumber || ''}
                        </span>
                    </div>
                </button>
                <Tippy
                    content={
                        <div>
                            <div className='asset-overview__online-text'>
                                {translate(
                                    'ABB.Powertrain.Frontend.gatewayStatusLabel'
                                )}{' '}
                                {gatewayItem?.connectivityStatus ===
                                    assetOnlineStatus.ONLINE
                                    ? translate(
                                        'ABB.Powertrain.Frontend.gatewayStatusOnlineLabel'
                                    )
                                    : translate(
                                        'ABB.Powertrain.Frontend.gatewayStatusOfflinLabel'
                                    )}
                            </div>
                            <div className='asset-overview__last-measurement'>
                                {translate('ABB.Powertrain.Frontend.labelLastStatusChange')}{' '}
                                {getTimeSinceDateString(
                                    gatewayItem.statusChangeTimestamp
                                )}
                            </div>
                        </div>
                    }
                    interactive
                    interactiveBorder={10}
                    animation='scale'
                    theme='light-border'
                    trigger='click'
                    placement='right'
                >
                    <div className='info-icon'>
                        {renderConnectivityIcon(gatewayItem?.summaryConnectionStatus)}
                    </div>
                </Tippy>
            </li>
        );
    };

    return (
        <div className='gateway-list-management'>
            {loadingStatus === apiCallStatus.SUCCESS ?
                <ul className='gateway-list'>
                    {renderGatewayItems()}
                </ul>
                : <LoadingIndicator />
            }
        </div>
    );
};

GatewayListManagementComponent.propTypes = {
    gatewayDetails: PropTypes.shape({
        gatewayList: PropTypes.arrayOf(
            PropTypes.shape({
                gatewayIdentifier: PropTypes.number,
                gatewayName: PropTypes.string,
                gatewaySerialNumber: PropTypes.string,
                connectivityStatus: PropTypes.string,
                statusChangeTimestamp: PropTypes.string,
            })
        ),
        loadingStatus: PropTypes.string,
    }),
    isShowGatewayDetails: PropTypes.bool,
    gatewayID: PropTypes.number,
    searchQueries: PropTypes.string,
    onSelectedGatewayData: PropTypes.func
};

export default GatewayListManagementComponent;
